/* eslint-disable max-len */
import React from 'react';
import { connect, useSelector } from 'react-redux';
import { makeStyles, darken } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
// import { useTranslation } from 'react-i18next';
// import PeopleIcon from '@material-ui/icons/People';
import clsx from 'clsx';
import Block from './Block';
import DownloadActivity from '../DownloadActivity';
import { selectEventTimeFormat, selectEventTimezone } from '../../helpers/selectors';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import ActivityLocation from '../ActivityLocation';
import useStringTransforms from '../../hooks/useStringTransforms';
// import RegisteredIcon from '../../assets/Registered';
import ActivityFill from '../../assets/ActivityFilledIcon';
import ScheduleCheck from '../../assets/scheduleCheckedIcon';
import { baseMoment, toAPStyleTime } from '../../helpers/dates';
import { WORKSHOP_CATEGORIES } from '../../helpers/constants';

const customWorkshopsColor = ['302853', '302862'];

const useStyles = makeStyles((theme) => ({
  secondaryLineContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(0.5),
  },
  secondaryLine: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
  },
  tertiaryLine: {
    fontFamily: 'Brilliant Cut Pro',
    fontWeight: 700,
    fontSize: 12,
    lineHeight: '100%',
    textTransform: 'uppercase',
    letterSpacing: 0,
    paddingTop: theme.spacing(0.25),
    '& span': {
      color: theme.palette.primary.main,
    },
  },
  lineRegistered: {
    color: '#C10016',
    '& span': {
      color: '#C10016',
    },
  },
  registered: {},
  umbrellaRegisterLabel: {
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'Brilliant Cut Pro',
    fontStyle: 'italic',
    color: '#C10016',
    letterSpacing: '0.04em',
    marginTop: theme.spacing(0.5),
  },
  customWorkshop: {
    backgroundColor: '#f0ead7',
  },
  tooltipSummary: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 600,
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  location: {
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 14,
    color: 'inherit',
  },
  divider: {
    fontWeight: 'normal',
    margin: theme.spacing(0, 2),
  },
  registeredText: {
    textTransform: 'uppercase',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'Avenir Next',
    fontStyle: 'italic',
    color: theme.palette.secondary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: 10,
    },
  },
  registeredTextInline: {
    textTransform: 'uppercase',
    fontSize: 12,
    fontFamily: 'Baskerville Old',
    fontStyle: 'italic',
    verticalAlign: 'middle',
  },
  speakersLine: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
  },
  speakers: {
    lineHeight: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 400,
    fontSize: 17,
    color: 'rgba(123, 118, 118, 1)',
    fontFamily: 'Fancy Cut Pro',
    letterSpacing: 0,
  },
  locationText: {
    lineHeight: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 400,
    fontSize: 12,
    color: 'rgba(123, 118, 118, 1)',
    fontFamily: 'Brilliant Cut Pro',
    textTransform: 'uppercase',
    letterSpacing: 0,
  },
  summaryLine: {
    display: 'flex',
    alignItems: 'center',
  },
  summaryLineText: {
    display: 'flex',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  nameLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  agendaIcon: {
    fontSize: 21,
    flexShrink: 0,
  },
  textColorPrimary: {
    color: theme.palette.primary.main,
  },
  timeStyles: {
    color: theme.palette.common.white,
    [theme.breakpoints.down('sm')]: {
    },
  },
  plenieres: {
    backgroundColor: '#768f77',
    color: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: darken('#768f77', 0.1),
    },
  },
  workshops: {
    backgroundColor: '#eef2e4',
    color: theme.palette.darkGrey.main,
    '&:hover': {
      backgroundColor: darken('#eef2e4', 0.1),
    },
  },
  soirees: {
    backgroundColor: '#dde4c9',
    color: theme.palette.darkGrey.main,
    '&:hover': {
      backgroundColor: darken('#dde4c9', 0.1),
    },
  },
  activites: {
    backgroundColor: '#e7e7e7',
    color: theme.palette.darkGrey.main,
    '&:hover': {
      backgroundColor: darken('#e7e7e7', 0.1),
    },
  },
}));

const Workshop = ({
  name, registered, id, allSpeakers, categories, workshopCategories, ...workshop
}) => {
  const classes = useStyles();
  const timeFormat = useSelector(selectEventTimeFormat());
  const eventTimezone = useSelector(selectEventTimezone);
  const { toTitleCase } = useStringTransforms();
  // const { t } = useTranslation(['stage', 'global', 'events', 'schedule']);

  const { speakers } = workshop;
  const workshopSpeakers = speakers?.filter(({ id: speakerId }) => allSpeakers[speakerId]);

  let peopleElement;
  if (workshopSpeakers.length > 1) peopleElement = workshopSpeakers.map(
    ({ id: speakerId }) => allSpeakers[speakerId]?.fullName
  ).join(', ');
  else if (workshopSpeakers.length === 1) {
    const { fullName, position } = allSpeakers[workshopSpeakers[0].id];
    const pipe = position ? ' , ' : '';
    // const at = position && company ? ` ${t('global:prepositions.at')} ` : '';
    const fullTitle = (position) ? `${toTitleCase(position, true)}` : '';
    peopleElement = `${toTitleCase(fullName)}${pipe}${fullTitle}`;
    // peopleElement = toTitleCase(fullName);
  }

  const locationInfo = (
    <ActivityLocation activity={workshop} className={classes.location} />
  );

  const isMandatory = workshopCategories.some(
    ({ id: wId }) => wId === WORKSHOP_CATEGORIES.mandatory
  );

  // const isPleniere = workshopCategories.some(
  //   ({ id: wId }) => wId === WORKSHOP_CATEGORIES.plenieres
  // );
  // const isWorkshop = workshopCategories.some(
  //   ({ id: wId }) => wId === WORKSHOP_CATEGORIES.workshops
  // );
  // const isSoiree = workshopCategories.some(
  //   ({ id: wId }) => wId === WORKSHOP_CATEGORIES.soirees
  // );
  // const isActivite = workshopCategories.some(
  //   ({ id: wId }) => wId === WORKSHOP_CATEGORIES.activites
  // );
  const isUmbrella = workshopCategories.some(
    ({ id: wId }) => wId === WORKSHOP_CATEGORIES.umbrella
  );
  const showOnRegister = workshop?.showOnRegister || isMandatory;

  const mainLine = undefined;

  // const summaryLine = (
  //   <div className={classes.summaryLine}>
  //     {/* {!registered && (
  //       <RegisteredIcon />
  //     )} */}
  //     <span className={classes.summaryLineText}>
  //       {name}
  //       <span className={classes.divider}>|</span>
  //       {locationInfo}
  //     </span>
  //   </div>
  // );

  const tooltipContent = (
    <>
      {locationInfo}
    </>
  );

  const durationInMinutes = baseMoment(workshop.endDate).diff(workshop.startDate, 'minutes');
  const isShortBlock = durationInMinutes <= 15;
  const isMediumBlock = durationInMinutes > 15 && durationInMinutes <= 30;

  const tertiaryLine = (
    <div
      className={clsx(
        classes.tertiaryLine,
        !!registered && classes.lineRegistered
      )}
    >
      {isShortBlock ? (
        <div className={classes.nameLine}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {name},
            {' '}
            <span style={{ textTransform: 'none' }}>{toAPStyleTime(baseMoment.utc(workshop.startDate).tz(eventTimezone).format(timeFormat))}</span>
          </span>
          {showOnRegister && (
            registered
              ? <ActivityFill color="primary" className={classes.agendaIcon} />
              : <ScheduleCheck color="primary" className={classes.agendaIcon} />
          )}
        </div>
      ) : (
        <>
          <span style={{ textTransform: 'none' }}>
            {toAPStyleTime(baseMoment.utc(workshop.startDate).tz(eventTimezone).format(timeFormat))}
            {' - '}
            {toAPStyleTime(baseMoment.utc(workshop.endDate).tz(eventTimezone).format(timeFormat))}
          </span>
          <div className={classes.nameLine} style={{ marginTop: 4 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </div>
            {showOnRegister && (
              registered
                ? <ActivityFill color="primary" className={classes.agendaIcon} />
                : <ScheduleCheck color="primary" className={classes.agendaIcon} />
            )}
          </div>
          {isMediumBlock && workshop.location && (
            <Typography className={classes.locationText} style={{ marginTop: 4 }}>
              {workshop.location}
            </Typography>
          )}
        </>
      )}
      {isUmbrella && (
        <Typography className={classes.umbrellaRegisterLabel}>
          click here to register
        </Typography>
      )}
    </div>
  );

  const secondaryLine = (peopleElement || workshop.location) ? (
    <div className={classes.speakersLine}>
      {peopleElement && (
        <Typography className={classes.speakers}>{peopleElement}</Typography>
      )}
      {workshop.location && (
        <Typography className={classes.locationText}>{workshop.location}</Typography>
      )}
    </div>
  ) : undefined;

  // const startHourLine = (
  //   <span>
  //     {` ${baseMoment(workshop.startDate).format(timeFormat)} -
  // ${baseMoment(workshop.endDate).format(timeFormat)}`}
  //   </span>
  // );

  return (
    <Block
      {...workshop}
      className={clsx(
        registered ? classes.registered : undefined,
        customWorkshopsColor.includes(id) && classes.customWorkshop
        // isPleniere && classes.plenieres,
        // isWorkshop && classes.workshops,
        // isSoiree && classes.soirees,
        // isActivite && classes.activites
      )}
      downloadActivity={(
        <DownloadActivity
          color={registered ? 'primary' : 'white'}
          // add id???
        />
      )}
      tooltipTitle={tooltipContent}
      mainLine={mainLine}
      secondaryLine={secondaryLine}
      tertiaryLine={tertiaryLine}
      summaryLine={undefined}
      hourLine={undefined}
      optional={workshop.optional}
      showRegister={showOnRegister}
      registered={registered}
      // location={workshop.location}
      // workshopSpeakers={workshop.speakers}
      // speakers={allSpeakers}
      // umbrella={isUmbrella}
    />
  );
};

Workshop.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  registered: PropTypes.bool,
  allSpeakers: PropTypes.objectOf(
    CustomPropTypes.speaker
  ),
  categories: PropTypes.objectOf(
    CustomPropTypes.workshopCategory
  ),
  workshopCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
};

Workshop.defaultProps = {
  registered: false,
  allSpeakers: {},
  categories: {},
  workshopCategories: [],
};

const mapStateToProps = ({
  entities: {
    speakers: allSpeakers,
    workshopCategories,
  },
}) => ({
  allSpeakers,
  categories: workshopCategories,
});

export default connect(mapStateToProps, null)(Workshop);
